/**
 * Tela de login — a porta quando APP_PASSWORD está definida no servidor.
 *
 * Proteção de demo: senha única, trocada por um token de sessão que vale para
 * todas as abas. Sem senha configurada no servidor, esta tela redireciona
 * para o menu sozinha.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Logo } from '../components/Logo'
import { api } from '../lib/api'
import { setToken } from '../lib/auth'
import { color, radius, shadow } from '../theme/tokens'

export function Login() {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Servidor sem senha → nada a pedir aqui. Em caso de erro na consulta,
    // fica no formulário: pior caso é um login desnecessário, nunca uma porta
    // aberta.
    api
      .authStatus()
      .then((status) => {
        if (!status.required) navigate('/', { replace: true })
      })
      .catch(() => undefined)
  }, [navigate])

  const submit = useCallback(async () => {
    if (!password || busy) return
    setBusy(true)
    setError(null)
    try {
      const { token } = await api.authLogin(password)
      setToken(token)
      navigate('/', { replace: true })
    } catch {
      setError('Senha incorreta.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }, [password, busy, navigate])

  return (
    <div
      style={{
        minHeight: '100vh',
        background: color.panel,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 360,
          maxWidth: '100%',
          background: '#1b2330',
          border: '1px solid rgba(255,255,255,.09)',
          borderRadius: radius.panel,
          boxShadow: shadow.modal,
          padding: '28px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo size={32} />
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>
              Hub de Cobrança <span style={{ color: color.brand }}>· IA</span>
            </div>
            <div style={{ fontSize: 11.5, color: color.panelTextFaint, marginTop: 2 }}>
              Acesso restrito à demonstração
            </div>
          </div>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 11.5, color: color.panelTextMuted, fontWeight: 600 }}>
            Senha de acesso
          </span>
          <input
            type="password"
            value={password}
            autoFocus
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit()
            }}
            style={{
              padding: '12px 14px',
              borderRadius: radius.lg,
              border: `1px solid ${color.panelBorder}`,
              background: color.panelRaised,
              color: '#fff',
              fontSize: 14,
              outline: 'none',
            }}
          />
        </label>

        {error ? (
          <div
            style={{
              fontSize: 12,
              color: '#ffd9cf',
              background: 'rgba(237,79,46,.14)',
              border: '1px solid rgba(237,79,46,.35)',
              borderRadius: radius.md,
              padding: '9px 12px',
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => void submit()}
          disabled={!password || busy}
          style={{
            background: color.brand,
            color: '#fff',
            border: 'none',
            borderRadius: radius.pill,
            padding: '13px 22px',
            fontSize: 14.5,
            fontWeight: 700,
            cursor: password && !busy ? 'pointer' : 'not-allowed',
            opacity: password && !busy ? 1 : 0.6,
          }}
        >
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </div>
    </div>
  )
}
